import {
  BadgeCheck,
  Braces,
  FileCode2,
  FileSearch,
  FileSpreadsheet,
  Highlighter,
  Link2,
  PenLine,
  Tags,
} from 'lucide-react';
import {
  buildToolGroups,
  defineStudioTool,
  mergeExtraTools,
  type StudioTool,
  type StudioToolGroup,
} from '@okrapdf/pdf-studio-plugin/studio';
import { TOOLS_BY_SLUG } from '@/features/tools';

/**
 * Host-injected studio tools (W.4.39 cloud, W.4.40 local).
 *
 * These runners can't live in the shared plugin registry — the package must
 * not import host code. Instead the host injects them via `StudioApp
 * extraTools`; they get the same rail highlight, `?tool=` deep links, and ⌘K
 * palette selection as built-in tools (e.g. compare) instead of navigating
 * away to a standalone runner page. Every tool surfaced on the studio landing
 * opens inside the workbench; a registry entry that can't (no runner at all)
 * is not a Studio tool, so the ⌘K palette never lists it.
 *
 * Panels wrap the exact runners the public `/tools/<slug>` pages render — one
 * runner, two shells. Ids match the public slugs so `studioToolIdForSlug` is
 * the identity map for every entry here.
 *
 * `hideFromHomeGrid`: cloud tools stay out of the home grid because the
 * workbench/landing home presents them in the CloudToolsSection (with the
 * "runs on <brand> servers" disclosure). Local extras behave like built-ins
 * and list in the grid.
 */

function extraTool(
  slug: string,
  icon: StudioTool['icon'],
  category: StudioTool['category'],
  keywords: string[],
  options: { hideFromHomeGrid: boolean; requiresDocument?: boolean; title?: string },
  load: StudioTool['load'],
): StudioTool {
  const def = TOOLS_BY_SLUG[slug];
  if (!def) throw new Error(`studio extra tool missing from the tools registry: ${slug}`);
  return defineStudioTool({
    id: slug,
    title: options.title ?? def.name,
    description: def.description,
    icon,
    category,
    keywords,
    requiresDocument: options.requiresDocument ?? false,
    hideFromHomeGrid: options.hideFromHomeGrid,
    load,
  });
}

const cloudTool = (
  slug: string,
  icon: StudioTool['icon'],
  category: StudioTool['category'],
  keywords: string[],
  load: StudioTool['load'],
  options?: { requiresDocument?: boolean; title?: string },
) =>
  extraTool(
    slug,
    icon,
    category,
    keywords,
    { hideFromHomeGrid: true, requiresDocument: options?.requiresDocument, title: options?.title },
    load,
  );

/** Cloud-backed tools (`/api/tools/*`, `/s`, hosted publish) — W.4.39. */
export const CLOUD_STUDIO_TOOLS: StudioTool[] = [
  cloudTool(
    'pdf-to-markdown',
    FileCode2,
    'convert',
    ['markdown', 'md', 'rag', 'llm', 'notes', 'parsebench'],
    () => import('./panels/pdf-to-markdown-panel'),
    { requiresDocument: true },
  ),
  // Replaces the plugin's local row-guess panel of the same id: an extra tool
  // wins over a built-in one, so the Studio and `/tools/pdf-to-excel` run the
  // same LlamaParse path (W.4.111).
  cloudTool(
    'pdf-to-excel',
    FileSpreadsheet,
    'convert',
    ['excel', 'xlsx', 'spreadsheet', 'tables', 'llamaparse'],
    () => import('./panels/pdf-to-excel-panel'),
    { requiresDocument: true },
  ),
  cloudTool(
    'pdf-to-json',
    Braces,
    'convert',
    ['json', 'structured', 'schema', 'extract'],
    () => import('./panels/pdf-to-json-panel'),
    { requiresDocument: true },
  ),
  cloudTool(
    'verapdf-checker',
    BadgeCheck,
    'analyze',
    ['verapdf', 'pdf/ua', 'accessibility', 'validation'],
    () => import('./panels/verapdf-checker-panel'),
  ),
  cloudTool(
    'matterhorn-pdf-checker',
    FileSearch,
    'analyze',
    ['matterhorn', 'preflight', 'accessibility'],
    () => import('./panels/matterhorn-pdf-checker-panel'),
  ),
  cloudTool(
    'well-tagged-pdf-checker',
    Tags,
    'analyze',
    ['tags', 'tagged', 'screen reader', 'accessibility'],
    () => import('./panels/well-tagged-pdf-checker-panel'),
  ),
  cloudTool(
    'pdf-to-html',
    FileCode2,
    'convert',
    ['html', 'web page', 'accessible'],
    () => import('./panels/pdf-to-html-panel'),
  ),
  cloudTool(
    'pdf-to-link',
    Link2,
    'convert',
    ['share', 'link', 'host', 'publish'],
    () => import('./panels/pdf-to-link-panel'),
    // The name the home's chip and the share dialog give it, so the Studio has
    // one name for it. `PDF to Link` stays the name of the public page.
    { title: 'Share link' },
  ),
  cloudTool(
    'handwriting-ocr',
    PenLine,
    'convert',
    ['handwriting', 'ocr', 'handwritten'],
    () => import('./panels/handwriting-ocr-panel'),
  ),
];

/** Local runners/panels that only existed as `/tools/<slug>` pages — W.4.40+. */
export const LOCAL_STUDIO_TOOLS: StudioTool[] = [
  // Highlight is canvas-native (W.4.41): it edits the studio's open document
  // on the main viewport, so it joins the requiresDocument guard like the
  // built-in edit tools.
  extraTool(
    'highlight',
    Highlighter,
    'edit',
    ['highlight', 'annotate', 'marker'],
    {
      hideFromHomeGrid: false,
      requiresDocument: true,
    },
    () => import('./panels/highlight-panel'),
  ),
];

/** Everything the host app injects into the Studio workbench + landing. */
export const HOST_EXTRA_TOOLS: StudioTool[] = [...CLOUD_STUDIO_TOOLS, ...LOCAL_STUDIO_TOOLS];

/**
 * The catalogue the ⌘K palette lists (W.4.132). The palette is mounted outside
 * any Studio, so this builds what All tools shows from the options every web
 * host gives the Studio; `ai` tools have no group, so the Agent changes nothing
 * here. It is the same list on every host: only where a pick opens differs.
 */
export function paletteToolGroups(): StudioToolGroup[] {
  return buildToolGroups(mergeExtraTools(HOST_EXTRA_TOOLS).filter((tool) => !tool.requiresCompute));
}
